import { inject } from '@angular/core';
import { signalStore, withMethods } from '@ngrx/signals';
import { withEntities } from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { CatchmentWorkDataFacadeService } from './catchment-work-data-facade.service';
// Self-contained (like every data-access lib): the store speaks the Orval-GENERATED
// types, NOT `@dgacontrol-frontend/core` (a buildable lib can't import the non-buildable
// core barrel → TS6059). Apps bridge generated↔core at their boundary.
import type {
  CatchmentWorkDataV4LoraDownlinksListParams,
  LogsLoraDownlinkGetPendingDataV4,
  SendDownlinkLoraByDateRangeRequestRequest,
} from './generated/types';

/**
 * Admin store for the LoRa "get pending data" downlink feature:
 *
 *  - `dt-catchment-work-logs-lora-downlink-get-pending-data` (history table) reads
 *    `entities()`/`isLoading()`/`nextCursor()` and drives `load(params)`/`loadMore()`.
 *  - `send-downlink-lora-get-pending-data` (action form) calls `sendDownlinkByDateRange`.
 *
 * Backed by the v4 cursor endpoint `/v4/catchment_work_data/lora_downlinks/` (ordered `-id`
 * desc; response shape `{results, next, previous}`). Every filter is a typed query param —
 * `?catchment_work=` (by identity), `?user=`, `?from_date=`/`?to_date=`; the old
 * `filter_parameters` JSON blob is retired and now returns HTTP 400 (V-cwd-1 closed).
 *
 * `load`/`loadMore` + cursor state (`nextCursor`/`previousCursor`/`isLoading`/`isLoadingMore`/
 * `error`/`requestVersion`) come from the shared `withCursorPagination` feature. The list is
 * FILTERABLE (user / date-range / obra) → each filter change calls `load(params)` (switchMap
 * resets to page 1); scroll near end calls `loadMore()` (exhaustMap appends next page).
 *
 * NOT a cache: parameterized live query, so no `onInit` and no `loaded` flag — the component
 * drives `load(params)` on init and on every filter change. The mutation returns its Observable
 * so the form component can sequence its UI; the error propagates to the global
 * `httpErrorInterceptor` (F1).
 */
export const CatchmentWorkLoraDownlinkStore = signalStore(
  withEntities<LogsLoraDownlinkGetPendingDataV4>(),
  withCursorPagination<
    LogsLoraDownlinkGetPendingDataV4,
    CatchmentWorkDataV4LoraDownlinksListParams
  >(() => {
    const facade = inject(CatchmentWorkDataFacadeService);
    return (query, cursor) =>
      facade.getLoraDownlinksV4({ ...query, cursor: cursor ?? undefined });
  }),
  withMethods((_store, facade = inject(CatchmentWorkDataFacadeService)) => ({
    /**
     * Trigger a LoRa downlink for every pending measurement of an obra in a date range.
     * Fire-and-forget action (no list to mutate): returns the Observable so the form
     * component shows the success toast and the error reaches the F1 interceptor.
     */
    sendDownlinkByDateRange(body: SendDownlinkLoraByDateRangeRequestRequest) {
      return facade.sendDownlinkLoraGetPendingDataByDateRange(body);
    },
  })),
);
